import { useEffect, useState, useMemo } from "react";
import { Table, Badge, Spinner, Button } from "reactstrap";
import { saveAs } from "file-saver";
import { getUtilityAccountsReport, exportUtilityAccountsExcel } from "../TenantForm/Helpers/api";

const STATUS = {
  COMPLETE: { color: "success", label: "Complete" },
  INCOMPLETE: { color: "danger", label: "Incomplete" },
  NOT_REQUIRED: { color: "secondary", label: "No accounts required" },
};

const UTILITY_KEYS = ["heat", "water", "electricity"];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const UtilityCell = ({ cell }) => {
  if (!cell) return <span className="text-muted">—</span>;
  if (cell.required && !cell.entered) {
    return <Badge color="danger">Missing</Badge>;
  }
  if (cell.entered) {
    return <span>{cell.value}</span>;
  }
  // No account needed (included in rent) — show whatever note exists, e.g. "Included".
  return <span className="text-muted">{cell.value || "—"}</span>;
};

const UtilityAccountsTab = ({ masterSheetData }) => {
  const mastersheetId = masterSheetData?._id;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!mastersheetId) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    getUtilityAccountsReport(mastersheetId)
      .then((resp) => {
        if (!active) return;
        if (resp?.success) setReport(resp.data);
        else setError(resp?.message || "Failed to load report");
      })
      .catch((e) => {
        if (active) setError(e?.response?.data?.message || "Failed to load report");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mastersheetId]);

  const visibleRows = useMemo(() => {
    if (!report?.rows) return [];
    return onlyIncomplete ? report.rows.filter((r) => r.status === "INCOMPLETE") : report.rows;
  }, [report, onlyIncomplete]);

  const includedLabel = useMemo(() => {
    if (!report?.utilitiesAvailable) return "Unknown";
    const list = UTILITY_KEYS.filter((k) => report.utilitiesAvailable[k]).map(cap);
    return list.length ? list.join(", ") : "None";
  }, [report]);

  const handleDownload = async () => {
    if (!mastersheetId) return;
    setDownloading(true);
    try {
      const blob = await exportUtilityAccountsExcel(mastersheetId);
      const safe = (report?.building_name || "report").replace(/[^a-z0-9_-]+/gi, "_");
      saveAs(blob, `Utility-Accounts_${safe}.xlsx`);
    } catch (e) {
      setError("Failed to download Excel.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading)
    return (
      <div className="text-center p-4">
        <Spinner color="primary" /> Building report…
      </div>
    );
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!report) return <p className="text-muted text-center">No data available.</p>;

  const t = report.totals;

  return (
    <div>
      <p className="mb-3">
        <strong>Utilities included in rent for this building:</strong>{" "}
        <span className="text-primary">{includedLabel}</span>
        <span className="text-muted ms-2 small">
          (units must have a real account number for each utility NOT included in rent — N/A is not accepted)
        </span>
      </p>

      <div className="d-flex flex-wrap gap-3 mb-3">
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">Units assessed</div>
          <strong>{t.unitsTotal}</strong>
        </div>
        <div className="border rounded p-2 px-3" style={{ background: "#e6f4ea" }}>
          <div className="small text-muted">Complete</div>
          <strong>{t.complete}</strong>
        </div>
        <div className="border rounded p-2 px-3" style={{ background: "#fdeaea" }}>
          <div className="small text-muted">Incomplete</div>
          <strong>{t.incomplete}</strong>
        </div>
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">No accounts required</div>
          <strong>{t.notRequired}</strong>
        </div>
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">Missing (H / W / E)</div>
          <strong>
            {t.missingByUtility.heat} / {t.missingByUtility.water} / {t.missingByUtility.electricity}
          </strong>
        </div>
      </div>

      <div className="d-flex align-items-center mb-2">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="utilOnlyIncomplete"
            checked={onlyIncomplete}
            onChange={(e) => setOnlyIncomplete(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="utilOnlyIncomplete">
            Show only incomplete
          </label>
        </div>
        <Button color="success" className="ms-auto" onClick={handleDownload} disabled={downloading || !mastersheetId}>
          {downloading ? <Spinner size="sm" /> : "Download Excel"}
        </Button>
      </div>

      <Table hover responsive className="table-sm">
        <thead className="table-light">
          <tr>
            <th>Unit #</th>
            <th>Tenant</th>
            <th>Heat{report.utilitiesAvailable?.heat === false ? " *" : ""}</th>
            <th>Water{report.utilitiesAvailable?.water === false ? " *" : ""}</th>
            <th>Electricity{report.utilitiesAvailable?.electricity === false ? " *" : ""}</th>
            <th>Missing</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.length ? (
            visibleRows.map((r, i) => (
              <tr key={`${r.unitNo}-${i}`} className={r.status === "INCOMPLETE" ? "table-danger" : ""}>
                <td>
                  <strong>{r.unitNo}</strong>
                </td>
                <td>{r.tenant}</td>
                <td>
                  <UtilityCell cell={r.heat} />
                </td>
                <td>
                  <UtilityCell cell={r.water} />
                </td>
                <td>
                  <UtilityCell cell={r.electricity} />
                </td>
                <td>
                  {r.missingUtilities.length ? (
                    <span className="text-danger">{r.missingUtilities.map(cap).join(", ")}</span>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <Badge color={STATUS[r.status]?.color ?? "secondary"}>
                    {STATUS[r.status]?.label ?? r.status}
                  </Badge>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="text-center text-muted">
                No units to display.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export { UtilityAccountsTab };
